'use client';

import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';

import Header from './components/Header';
import OtterCTA from './components/OtterCTA';
import Hero from './components/Hero';
import WhySoftWater from './components/WhySoftWater';
import HowItWorks from './components/HowItWorks';
import SavingsCalculator from './components/SavingsCalculator';
import Contact from './components/Contact';
import Footer from './components/Footer';

/** ----- WP endpoint ----- */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

/** ----- Minimal Marketing shape (only what we actually used) ----- */
type Marketing = Partial<{
  hero: { title?: string; sub?: string; cta?: { text?: string; href?: string } };
  sections: Partial<{
    why_heading: string;
    why_sub: string;
    why_blocks: { id?: string; title?: string; body?: string; list?: string[] }[];
    how_heading: string;
    how_sub: string;
    how_steps: { number?: string; title?: string; description?: string; list?: string[] }[];
    how_regeneration_note: string;
    calc_heading: string;
    calc_sub: string;
    calc_family_label: string;
    calc_family_helper: string;
    calc_hardness_label: string;
    calc_hardness_helper: string;
    calc_bucket_soap: string;
    calc_bucket_appliance: string;
    calc_bucket_total: string;
    calc_model_note: string;
    calc_savings_title: string;
    calc_why_saves_title: string;
    calc_why_saves_list: string[];
    calc_final_disclaimer: string;
    cta_label: string;
    cta_href: string;
    contact: Partial<{
      contact_title: string;
      contact_subtitle: string;
      contact_success_title: string;
      contact_success_subtitle: string;
      contact_button: string;
      contact_consent: string;
    }>;
  }>;
  components?: Partial<{
    imageCompare: { beforeLabel?: string; afterLabel?: string };
  }>;
  footer?: Partial<{
    brand: string;
    tagline: string;
    phone_pretty: string;
    phone_href: string;
    address: string;
    maps_href: string;
    social: { name: string; href: string; icon?: string }[];
  }>;
}>;

/** ----- GA helper: fire only when not embedded ----- */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return;
    const g = (window as any).gtag;
    if (typeof g === 'function') g('event', event, params || {});
  } catch {}
}

/** ----- Marketing context so children can consume WP copy without refetching ----- */
const MarketingContext = createContext<Marketing | null>(null);
export const useMarketing = () => useContext(MarketingContext);

/** ----- Page ----- */
const Page: React.FC = () => {
  const [marketing, setMarketing] = useState<Marketing | null>(null);

  // GA page-level
  useEffect(() => {
    ga('page_view', { page_title: 'WET Landing', page_path: '/microgrid/wet' });
    ga('site_render', { site: 'wet', route: '/microgrid/wet' });
  }, []);

  // Fetch WP once and share via context
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = (await r.json()) as Marketing;
        if (!cancelled) setMarketing(data || {});
      } catch {
        /* noop: children will fall back to their defaults */
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Stable value to avoid unnecessary rerenders
  const ctx = useMemo(() => marketing, [marketing]);

  return (
    <MarketingContext.Provider value={ctx}>
      <Header />
     <OtterCTA />
      <main>
        <Hero />
        <WhySoftWater />
        <HowItWorks />
        <SavingsCalculator />
        <Contact />
      </main>
      <Footer />
    </MarketingContext.Provider>
  );
};

export default Page;
